// Git diff → changed-symbol mapping (proposal §7.1, Phase F).
//
// Supports `agent-analyzer impact --diff <ref>`: run git to discover which files
// (and which line ranges) changed relative to a ref, then map those line ranges
// onto the symbol nodes of the graph to derive symbol-level seeds for impact
// analysis. When a changed line falls outside every symbol (e.g. a module-level
// import edit), the containing file is seeded instead so file-level edges still
// propagate.
//
// git is invoked with `--relative`, so reported paths are relative to the
// project root (cwd) and line up with the graph's node ids. Symbol mapping
// prefers the smallest (most specific) enclosing symbol per changed line.

import { execFileSync } from "node:child_process";

import type { AstNode } from "../ast/types.js";
import type { DependencyGraph } from "../dependency/types.js";
import { AnalysisError } from "./project.js";

/** A contiguous range of 1-based line numbers in a file's post-change state. */
export interface LineRange {
  start: number;
  end: number;
}

/** Run a git command in `cwd`, returning stdout. Throws AnalysisError on failure. */
function runGit(args: string[], cwd: string): string {
  try {
    return execFileSync("git", args, {
      cwd,
      encoding: "utf8",
      maxBuffer: 64 * 1024 * 1024,
      stdio: ["ignore", "pipe", "pipe"],
    });
  } catch (error) {
    const message = (error as { stderr?: string; message?: string }).stderr
      ? String((error as { stderr?: string }).stderr).trim()
      : ((error as Error).message ?? "git command failed");
    throw new AnalysisError(`git ${args.join(" ")}: ${message}`);
  }
}

/** Changed file paths (project-relative) between `ref` and the working tree. */
export function getChangedFiles(ref: string, cwd: string): string[] {
  const out = runGit(["diff", "--relative", "--name-only", ref], cwd);
  return out
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

const HUNK_HEADER = /^@@ -\d+(?:,\d+)? \+(\d+)(?:,(\d+))? @@/;

/**
 * Parse `git diff --relative -U0 <ref>` into per-file changed line ranges (in
 * the post-change file). A hunk `@@ -a,b +c,d @@` covers new lines c..c+d-1;
 * a pure deletion (d = 0) is recorded as the single anchor line c so the symbol
 * around the deletion is still flagged.
 */
export function getChangedLineRanges(
  ref: string,
  cwd: string,
): Map<string, LineRange[]> {
  const out = runGit(["diff", "--relative", "-U0", ref], cwd);
  const ranges = new Map<string, LineRange[]>();

  let currentFile: string | undefined;
  for (const line of out.split("\n")) {
    if (line.startsWith("+++ ")) {
      const path = line.slice(4).trim();
      if (path === "/dev/null") {
        currentFile = undefined;
      } else {
        // Strip the "b/" prefix git adds to the post-change path.
        currentFile = path.startsWith("b/") ? path.slice(2) : path;
      }
      continue;
    }
    const match = HUNK_HEADER.exec(line);
    if (!match || !currentFile) continue;

    const start = Number.parseInt(match[1] as string, 10);
    const count = match[2] === undefined ? 1 : Number.parseInt(match[2], 10);
    const range: LineRange =
      count === 0 ? { start, end: start } : { start, end: start + count - 1 };

    const list = ranges.get(currentFile);
    if (list) list.push(range);
    else ranges.set(currentFile, [range]);
  }

  return ranges;
}

/** Width of a symbol's source range (used to pick the most specific one). */
function span(node: AstNode): number {
  const info = node.symbolInfo;
  return info ? info.endLine - info.line : Number.POSITIVE_INFINITY;
}

/**
 * Map changed line ranges to seed ids: the smallest enclosing symbol per
 * changed line, or the containing file when a line is outside every symbol.
 * Files with no symbol nodes at all seed the file path directly.
 */
export function mapChangedLinesToSymbols(
  graph: DependencyGraph,
  changed: Map<string, LineRange[]>,
): string[] {
  const symbolsByFile = new Map<string, AstNode[]>();
  for (const node of graph.ast.nodes.values()) {
    if (node.kind !== "symbol" || !node.symbolInfo) continue;
    const list = symbolsByFile.get(node.filePath);
    if (list) list.push(node);
    else symbolsByFile.set(node.filePath, [node]);
  }

  const seeds = new Set<string>();
  for (const [file, ranges] of changed) {
    const symbols = symbolsByFile.get(file);
    if (!symbols || symbols.length === 0) {
      seeds.add(file);
      continue;
    }

    for (const range of ranges) {
      for (let ln = range.start; ln <= range.end; ln += 1) {
        let best: AstNode | undefined;
        for (const node of symbols) {
          const info = node.symbolInfo;
          if (info && info.line <= ln && ln <= info.endLine) {
            if (!best || span(node) < span(best)) best = node;
          }
        }
        if (best?.symbolInfo) seeds.add(best.symbolInfo.symbolId);
        else seeds.add(file); // module-level change → seed the file.
      }
    }
  }

  return [...seeds];
}

/**
 * Resolve the impact seeds implied by a git diff against `ref`: changed line
 * ranges mapped to the smallest enclosing symbols (falling back to file paths).
 */
export function resolveDiffSeeds(
  graph: DependencyGraph,
  ref: string,
  cwd: string,
): string[] {
  const ranges = getChangedLineRanges(ref, cwd);
  if (ranges.size === 0) {
    // No textual hunks (e.g. binary/rename only): fall back to changed files.
    const files = getChangedFiles(ref, cwd);
    return files;
  }
  return mapChangedLinesToSymbols(graph, ranges);
}
